// Shared pieces for CRM records (accounts, opportunities, quotes): formatting, stages, and the
// expandable section, highlight strip and stage bar from the account page design.
import { useState, type ReactNode } from "react";

export const money = (n: number | null | undefined, digits = 0) =>
  n === null || n === undefined || isNaN(n) ? "—" : new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: digits, minimumFractionDigits: digits }).format(n);
export const compact = (n: number | null | undefined) =>
  n === null || n === undefined ? "—" : n >= 10000 ? "$" + new Intl.NumberFormat("en-US", { notation: "compact", maximumFractionDigits: 1 }).format(n) : money(n);
export const pct = (m: number | null | undefined) => (m === null || m === undefined ? "—" : `${(m * 100).toFixed(1)}%`);
export const day = (d: string | null | undefined) => (d ? new Date(d.length === 10 ? d + "T00:00:00" : d).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "—");
export const shortDay = (d: string | null | undefined) => (d ? new Date(d.length === 10 ? d + "T00:00:00" : d).toLocaleDateString("en-US", { month: "short", day: "numeric" }) : "—");
export const daysUntil = (d: string | null | undefined) => (d ? Math.round((new Date(d + "T00:00:00").getTime() - new Date(new Date().toDateString()).getTime()) / 864e5) : null);
export const today = () => new Date().toISOString().slice(0, 10);

export const STAGES = [
  { id: "qualify", name: "Qualify" }, { id: "develop", name: "Develop" }, { id: "propose", name: "Propose" }, { id: "negotiate", name: "Negotiate" },
];
export const STAGE_NAME: Record<string, string> = { qualify: "Qualify", develop: "Develop", propose: "Propose", negotiate: "Negotiate", won: "Won", lost: "Lost" };
export const STAGE_TONE: Record<string, string> = { qualify: "warn", develop: "info", propose: "info", negotiate: "info", won: "ok", lost: "" };

// What each stage needs before moving on (the checklist on the opportunity).
export const CHECKLIST: Record<string, { key: string; text: string }[]> = {
  qualify: [{ key: "need_confirmed", text: "Need and timing confirmed" }, { key: "budget_known", text: "Budget range known" }, { key: "decision_maker", text: "Decision maker identified" }],
  develop: [{ key: "requirements", text: "Requirements and drawings received" }, { key: "committee_mapped", text: "Buying committee mapped" }, { key: "estimate_costed", text: "Estimate costed from BOM and routing" }],
  propose: [{ key: "quote_sent", text: "Primary quote sent" }, { key: "objections", text: "Objections captured" }],
  negotiate: [{ key: "volume_confirmed", text: "Decision maker confirms volume and price" }, { key: "quality_signoff", text: "Quality / PPAP plan agreed" }, { key: "credit_checked", text: "Credit and terms checked" }],
};

export const ROLES = [
  { id: "decision_maker", name: "Decision maker", tone: "ok" }, { id: "influencer", name: "Influencer", tone: "info" }, { id: "champion", name: "Champion", tone: "ok" },
  { id: "user", name: "User", tone: "" }, { id: "blocker", name: "Blocker", tone: "danger" }, { id: "unknown", name: "Unknown", tone: "" },
];
export const roleOf = (id: string) => ROLES.find((r) => r.id === id) ?? ROLES[5];

export const QUOTE_TONE: Record<string, string> = { draft: "", approved: "info", sent: "warn", accepted: "ok", rejected: "danger", superseded: "" };
export const LOST_REASONS = ["Price", "Lead time", "Competitor", "No decision", "Quality concerns", "Requirements changed", "Other"];

/** A section that opens and closes in place; the summary line carries its rollup chips. */
export function XSection({ title, chips = [], open: initial = true, actions, note, children }: { title: string; chips?: (string | null | false | undefined)[]; open?: boolean; actions?: ReactNode; note?: ReactNode; children: ReactNode }) {
  const [open, setOpen] = useState(initial);
  return (
    <section className={`xsec ${open ? "open" : ""}`}>
      <div className="xsec-h">
        <button className="xsec-toggle" aria-expanded={open} onClick={() => setOpen(!open)}>
          <svg className="chev" width="14" height="14" viewBox="0 0 16 16" aria-hidden="true"><path d="M5 3l5 5-5 5" fill="none" stroke="currentColor" strokeWidth="1.6" /></svg>
          <h3>{title}</h3>
          <span className="xsec-chips">{chips.filter(Boolean).map((c) => <span key={c as string} className="xchip">{c}</span>)}</span>
        </button>
        {actions && <span className="xsec-actions">{actions}</span>}
      </div>
      {open && <div className="xsec-body">{note && <div className="xsec-note">{note}</div>}{children}</div>}
    </section>
  );
}

/** A row that expands in place to show its detail. */
export function XRow({ cells, children, cols }: { cells: ReactNode[]; children?: ReactNode; cols: string }) {
  const [open, setOpen] = useState(false);
  return (
    <div className={`xrow ${open ? "open" : ""}`}>
      <button className="xrow-h" style={{ gridTemplateColumns: `22px ${cols}` }} aria-expanded={open} onClick={() => setOpen(!open)} disabled={!children}>
        <svg className="chev" width="14" height="14" viewBox="0 0 16 16" aria-hidden="true" style={{ visibility: children ? "visible" : "hidden" }}><path d="M5 3l5 5-5 5" fill="none" stroke="currentColor" strokeWidth="1.6" /></svg>
        {cells.map((c, i) => <span key={i} className="xcell">{c}</span>)}
      </button>
      {open && children && <div className="xrow-body">{children}</div>}
    </div>
  );
}
export function XHead({ cols, labels, right = [] }: { cols: string; labels: string[]; right?: number[] }) {
  return <div className="xhead" style={{ gridTemplateColumns: `22px ${cols}` }}><span />{labels.map((l, i) => <span key={l + i} style={{ textAlign: right.includes(i) ? "right" : "left" }}>{l}</span>)}</div>;
}

export function Highlights({ items }: { items: { label: string; value: string; note?: string; tone?: "bad" | "warn" | "good"; href?: string }[] }) {
  return (
    <div className="hl-strip">
      {items.map((h) => (
        <div key={h.label} className="hl">
          <div className="hl-l">{h.label}</div>
          <div className={`hl-v ${h.tone ?? ""}`}>{h.value}</div>
          {h.note && <div className="hl-n">{h.note}</div>}
        </div>
      ))}
    </div>
  );
}

export function StageBar({ stages, current, closed, onPick, busy, extra }: {
  stages: { id: string; name: string; note?: string }[]; current: number; closed?: { label: string; tone: "ok" | "lost" } | null;
  onPick?: (id: string) => void; busy?: boolean; extra?: ReactNode;
}) {
  return (
    <div className="stagebar">
      <div className="stagebar-track" role="list">
        {stages.map((s, i) => {
          const cls = closed ? (closed.tone === "ok" ? "done" : i <= current ? "done" : "") : i < current ? "done" : i === current ? "current" : "";
          const can = !!onPick && !closed && i !== current && !busy;
          return (
            <button key={s.id} role="listitem" className={`sb-stage ${cls}`} disabled={!can} onClick={() => can && onPick!(s.id)} title={can ? `Move to ${s.name}` : s.name} aria-current={i === current && !closed ? "step" : undefined}>
              <span className="nm">{cls === "done" ? "✓ " : ""}{s.name}</span>
              {s.note && <span className="nt">{s.note}</span>}
            </button>
          );
        })}
        {closed && <span className={`sb-stage closed ${closed.tone}`} role="listitem"><span className="nm">{closed.label}</span></span>}
      </div>
      {extra}
    </div>
  );
}

export function Check({ done, text, meta, onToggle, disabled }: { done: boolean; text: string; meta?: string; onToggle?: () => void; disabled?: boolean }) {
  return (
    <label className={`check ${done ? "done" : ""}`}>
      <input type="checkbox" checked={done} disabled={disabled} onChange={onToggle} />
      <span className="t">{text}</span>
      {meta && <span className="m">{meta}</span>}
    </label>
  );
}
